import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "uikit";
import { isBoolean } from "lodash";

import Order, {
	OrderTransferType,
} from "../../../../../../../../../../services/Order";
import { TransferPayment } from "../../../../../../../../../../services/OrderTransfer";
import { useTypedSelector } from "../../../../../../../../../../redux/store";
import {
	Button,
	StyledRow,
	AccessGuard,
} from "../../../../../../../../../../components/common";
import { TabAccessNames, TabKeys } from "../../constants/access";

const OrderTransferButtonsBase: React.FC<OrderTransferButtonsBase.Props> = ({
	disabled,
	orderId,
	order,
}) => {
	const { t } = useTranslation();

	const settings = useTypedSelector(
		(state) => state.globalState.sendAvailable.data.subscription.basic,
	);

	const transfer = useMemo(
		() => order?.additionalFields?.transfer,
		[order?.additionalFields?.transfer],
	);

	const [loadingSendToTransferLocal, setLoadingSendToTransferLocal] =
		useState<boolean>(false);
	const [loadingSendToTransferGlobal, setLoadingSendToTransferGlobal] =
		useState<boolean>(false);
	const [loadingSendToTransferOutside, setLoadingSendToTransferOutside] =
		useState<boolean>(false);

	const lastReceivedOrder = useMemo(
		() => transfer?.lastReceivedOrder,
		[transfer?.lastReceivedOrder],
	);

	const carClassId = useMemo(() => {
		const orderId = order?.id;
		if (!orderId) return [];
		if (!order?.orderToCarClasses?.length) return [];
		return order.orderToCarClasses?.[0]?.carClass?.id;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order?.id, order?.orderToCarClasses?.[0]?.carClass?.id]);

	const sendAvailable = useMemo(() => {
		if (!settings?.length) return null;
		const taxiServiceId = order?.taxiService?.id;
		if (!taxiServiceId) return null;
		return settings.find((item) => item.taxiServiceId === taxiServiceId);
	}, [order?.taxiService?.id, settings]);

	const isNotEnabled = useMemo(() => {
		const taxiServiceId = order?.taxiService?.id;

		if (!taxiServiceId) return true;
		if (!sendAvailable) return true;
		if (!sendAvailable.active) return true;
		if (!carClassId) return true;
		if (!order?.paymentType) return true;

		if (
			!sendAvailable?.carClassIds?.length ||
			!sendAvailable?.carClassIds?.includes(carClassId)
		) {
			return true;
		}

		const paymentType: TransferPayment =
			order.paymentType as unknown as TransferPayment;

		if (!sendAvailable?.payments?.length) return true;

		const hasAgent =
			order?.additionalFields?.hasAgent && TransferPayment.AGENT;
		if (hasAgent && !sendAvailable?.payments?.includes(hasAgent)) {
			return true;
		}

		const hasDisc = order?.isCustomerDiscounted && TransferPayment.BONUS;
		if (hasDisc && !sendAvailable?.payments?.includes(hasDisc)) {
			return true;
		}

		if (!sendAvailable?.payments?.includes(paymentType)) {
			return true;
		}

		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		order?.taxiService?.id,
		order?.paymentType,
		order?.agent?.id,
		sendAvailable,
		carClassId,
	]);

	const currentTransferStage = useMemo(() => {
		const value = transfer?.sendProcess?.currentTransferStage;
		return value;
	}, [transfer]);

	const isNotOwn = useMemo(() => {
		const value = order?.isOwn;
		if (order?.transfer && !value) return true;
		return isBoolean(value) ? !value : false;
	}, [order?.isOwn, order?.transfer]);

	const hasExecutor = useMemo(() => {
		if (!order?.id) return true;
		const value =
			order?.executorToOrder?.length ||
			lastReceivedOrder?.executorToOrder?.length;
		return !!value;
	}, [
		lastReceivedOrder?.executorToOrder?.length,
		order?.executorToOrder?.length,
		order?.id,
	]);

	const isCurrentTransferStage = useMemo(() => {
		if (hasExecutor) return true;
		return !currentTransferStage;
	}, [currentTransferStage, hasExecutor]);

	const disabledOrder = useMemo(() => {
		if (!orderId) return true;
		return false;
	}, [orderId]);

	const returnFromTransfer = useCallback(async () => {
		if (!orderId) return;
		if (isNotOwn) return;
		if (hasExecutor) return;
		Order.returnFromTransfer(orderId);
	}, [orderId, isNotOwn, hasExecutor]);

	const sendToTransfer = useCallback(async () => {
		if (!orderId) return;
		if (isNotOwn) return;
		if (hasExecutor) return;
		Order.sendToTransfer(orderId);
	}, [hasExecutor, isNotOwn, orderId]);

	const sendToTransferLocal = useCallback(async () => {
		if (!orderId) return;
		if (isNotOwn) return;
		if (hasExecutor) return;
		if (loadingSendToTransferLocal) return;
		setLoadingSendToTransferLocal(true);
		await Order.sendToTransfer(orderId, { type: OrderTransferType.LOCAL });
		setLoadingSendToTransferLocal(false);
	}, [hasExecutor, isNotOwn, loadingSendToTransferLocal, orderId]);

	const sendToTransferGlobal = useCallback(async () => {
		if (!orderId) return;
		if (isNotOwn) return;
		if (hasExecutor) return;
		if (loadingSendToTransferGlobal) return;
		setLoadingSendToTransferGlobal(true);
		await Order.sendToTransfer(orderId, { type: OrderTransferType.GLOBAL });
		setLoadingSendToTransferGlobal(false);
	}, [hasExecutor, isNotOwn, loadingSendToTransferGlobal, orderId]);

	const sendToTransferOutside = useCallback(async () => {
		if (!orderId) return;
		if (isNotOwn) return;
		if (hasExecutor) return;
		if (loadingSendToTransferOutside) return;
		setLoadingSendToTransferOutside(true);
		await Order.sendToTransfer(orderId, {
			type: OrderTransferType.OUTSIDE,
		});
		setLoadingSendToTransferOutside(false);
	}, [hasExecutor, isNotOwn, loadingSendToTransferOutside, orderId]);

	const textBtnGroup = useMemo(() => {
		const linkLang = "pages.mainPage.pages.orders.orderHeader.secondRow.";

		return {
			orderReturnFromTransfer:
				t(`${linkLang}orderReturnFromTransfer`) ?? "",
			orderSendToTransfer: t(`${linkLang}orderSendToTransfer`) ?? "",
			orderSendToTransferLocal:
				t(`${linkLang}orderSendToTransferLocal`) ?? "",
			orderSendToTransferGlobal:
				t(`${linkLang}orderSendToTransferGlobal`) ?? "",
			orderSendToTransferOutside:
				t(`${linkLang}orderSendToTransferOutside`) ?? "",
		};
	}, [t]);

	return (
		<StyledRow gap="5px">
			<AccessGuard
				accessName={TabAccessNames[TabKeys.ORDER_RETURN_FROM_TRANSFER]}
			>
				<Button
					variant="secondary"
					// disabled={
					// 	disabled ||
					// 	disabledOrder ||
					// 	isCurrentTransferStage ||
					// 	hasExecutor ||
					// 	isNotOwn
					// }
					disabled={
						disabled ||
						disabledOrder ||
						isNotOwn ||
						isCurrentTransferStage
					}
					title={textBtnGroup.orderReturnFromTransfer}
					tabIndex={-1}
					onClick={returnFromTransfer}
				>
					<Icon id="return-back" size={18} />
				</Button>
			</AccessGuard>
			<AccessGuard
				accessName={TabAccessNames[TabKeys.ORDER_SEND_TO_TRANSFER]}
			>
				<Button
					variant="secondary"
					// disabled={
					// 	disabled ||
					// 	disabledOrder ||
					// 	hasExecutor ||
					// 	!!currentTransferStage ||
					// 	isNotOwn ||
					// 	isNotEnabled
					// }
					disabled={true}
					title={textBtnGroup.orderSendToTransfer}
					tabIndex={-1}
					onClick={sendToTransfer}
				>
					<Icon id="sendTransferSend" size={22} />
				</Button>
			</AccessGuard>
			<AccessGuard
				accessName={
					TabAccessNames[TabKeys.ORDER_SEND_TO_TRANSFER_LOCAL]
				}
			>
				<Button
					variant="secondary"
					disabled={
						disabled ||
						disabledOrder ||
						hasExecutor ||
						!sendAvailable?.isLocal ||
						currentTransferStage === OrderTransferType.LOCAL ||
						currentTransferStage === OrderTransferType.GLOBAL ||
						currentTransferStage === OrderTransferType.OUTSIDE ||
						isNotOwn ||
						isNotEnabled
					}
					title={textBtnGroup.orderSendToTransferLocal}
					tabIndex={-1}
					onClick={sendToTransferLocal}
				>
					<Icon id="sendTransferLocal" size={22} />
				</Button>
			</AccessGuard>
			<AccessGuard
				accessName={
					TabAccessNames[TabKeys.ORDER_SEND_TO_TRANSFER_GLOBAL]
				}
			>
				<Button
					variant="secondary"
					disabled={
						disabled ||
						disabledOrder ||
						hasExecutor ||
						!sendAvailable?.isGlobal ||
						currentTransferStage === OrderTransferType.GLOBAL ||
						currentTransferStage === OrderTransferType.OUTSIDE ||
						isNotOwn ||
						isNotEnabled
					}
					title={textBtnGroup.orderSendToTransferGlobal}
					tabIndex={-1}
					onClick={sendToTransferGlobal}
				>
					<Icon id="sendTransferGlobal" size={22} />
				</Button>
			</AccessGuard>
			<AccessGuard
				accessName={
					TabAccessNames[TabKeys.ORDER_SEND_TO_TRANSFER_OUTSIDE]
				}
			>
				<Button
					variant="secondary"
					disabled={
						disabled ||
						disabledOrder ||
						hasExecutor ||
						!sendAvailable?.isOutside ||
						currentTransferStage === OrderTransferType.OUTSIDE ||
						isNotOwn ||
						isNotEnabled
					}
					title={textBtnGroup.orderSendToTransferOutside}
					tabIndex={-1}
					onClick={sendToTransferOutside}
				>
					<Icon id="sendTransferOutside" size={22} />
				</Button>
			</AccessGuard>
		</StyledRow>
	);
};

export const OrderTransferButtons = memo(OrderTransferButtonsBase);

declare namespace OrderTransferButtonsBase {
	interface Props {
		orderId?: number;
		disabled?: boolean;
		order?: Order.Model;
	}
}

export default OrderTransferButtonsBase;
